/**
 * Top-bar pomodoro chip — and the one owner of Focus's live wiring.
 *
 * Always mounted (inside `Layout`), visible only while a focus or break
 * runs. It holds the single `focus:<owner uuid>` SSE subscription for the
 * whole app (a trailing-debounced invalidate of `["focus"]`) and mounts
 * `useFocusPhaseEffects` (wrap-up refetches, tab-title countdown, opt-in
 * alerts), so neither runs twice when the Focus page is open too.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { clsx } from "clsx";

import { FOCUS_KEYS, focusApi } from "../../api/focus";
import { isBreak } from "../../lib/focus";
import { formatClock } from "../../lib/focusTime";
import { useFocusPhaseEffects } from "../../lib/hooks/useFocusPhaseEffects";
import { useFocusPrefs } from "../../lib/hooks/useFocusPrefs";
import { useFocusTimer } from "../../lib/hooks/useFocusTimer";
import { useLiveUpdates, type LiveEvent } from "../../lib/hooks/useLiveUpdates";

const LIVE_DEBOUNCE_MS = 400;

export function FocusTimerChip() {
  const qc = useQueryClient();
  const timer = useFocusTimer();
  const [prefs] = useFocusPrefs();
  useFocusPhaseEffects({ snapshot: timer.snapshot, remainingMs: timer.remainingMs, prefs });

  const owner = useQuery({
    queryKey: FOCUS_KEYS.owner,
    queryFn: focusApi.owner,
    staleTime: Infinity,
    retry: false,
    enabled: timer.snapshot !== undefined,
  });
  const ownerId = owner.data?.user.id;
  const topics = useMemo(() => (ownerId ? [`focus:${ownerId}`] : []), [ownerId]);

  // Frames carry no state — any frame means "refetch". A timer start writes
  // a burst (timer + changes), so collapse it into one trailing invalidate.
  const pending = useRef<number | null>(null);
  const onEvent = useCallback(
    (ev: LiveEvent) => {
      if (ev.table !== "focus") return;
      if (pending.current !== null) window.clearTimeout(pending.current);
      pending.current = window.setTimeout(() => {
        pending.current = null;
        void qc.invalidateQueries({ queryKey: FOCUS_KEYS.all });
      }, LIVE_DEBOUNCE_MS);
    },
    [qc],
  );
  useEffect(
    () => () => {
      if (pending.current !== null) window.clearTimeout(pending.current);
    },
    [],
  );
  useLiveUpdates(topics, onEvent);

  const { snapshot, remainingMs, wrappingUp } = timer;
  if (!timer.running || !snapshot?.session || remainingMs === null) return null;

  const onBreak = isBreak(snapshot.phase);
  const clock = wrappingUp ? "0:00" : formatClock(Math.ceil(remainingMs / 1000));
  const label = onBreak
    ? snapshot.phase === "long_break"
      ? "long break"
      : "break"
    : (snapshot.session.goal_title ?? "focus");
  return (
    <Link
      to="/focus?view=today"
      className={clsx("focus-chip", onBreak && "is-break", wrappingUp && "is-wrapping")}
      title={wrappingUp ? "Wrapping up…" : onBreak ? "Break – open Focus" : "Focus running – open Focus"}
    >
      <span className="dot" />
      <span className="focus-chip-clock">{clock}</span>
      <span className="focus-chip-label">{label}</span>
    </Link>
  );
}
